import {
  Dialog,
  Label,
  RadioGroupControl,
  TextV2,
} from '@insights-ltd/design-library/components';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { styled } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { ProfileResponse, useSetPronounsAndPreferredDialect } from 'api';
import SelectDialectControl from 'components/SelectDialectControl';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getPronounOptions,
  isPronounSupported,
  type Pronoun,
} from 'domain/pronouns';
import { AllDialectOptions, Dialects } from 'types/dialects';
import { getProfileTitleText } from 'utils/generalUtils';
import { DialectMap, DIALECTS } from 'variables';
import { getProfileDialectOptions } from './ChangeLanguageAndPronounsModal.helper';

const StyledUL = styled('ul')(({ theme }) => ({
  paddingLeft: theme.spacing(4),
  marginTop: theme.spacing(1),
}));

const StyledLabel = styled(Label)(() => ({}));

export type ChangeLanguageAndPronounsModalProps = {
  open: boolean;
  onClose: () => void;
  organisationId: string;
  learnerId: string;
  evaluationId: string;
  pronoun: Pronoun;
  preferredDialect: Dialects;
  profiles: ProfileResponse[];
};

const ChangeLanguageAndPronounsModalContent = ({
  pronoun,
  setPronoun,
  setSelectedDialect,
  selectedDialect,
  profileTitles,
  profileDialectOptions,
  initialPronoun,
}: {
  pronoun: Pronoun;
  setPronoun: (value: Pronoun) => void;
  setSelectedDialect: (value: AllDialectOptions) => void;
  selectedDialect: AllDialectOptions;
  profileTitles: string[];
  profileDialectOptions: DialectMap[];
  initialPronoun: Pronoun;
}) => {
  const { t } = useTranslation();

  const handleDialectSelection = (selection: AllDialectOptions) => {
    if (!isPronounSupported(pronoun, DIALECTS[selection])) {
      if (isPronounSupported(initialPronoun, DIALECTS[selection])) {
        setPronoun(initialPronoun);
      } else {
        setPronoun('He');
      }
    }
    setSelectedDialect(selection);
  };

  const renderProfileTitles = (titles: string[]): JSX.Element => (
    <StyledUL sx={{ marginTop: 0 }}>
      {titles.map((title, idx) => (
        <li key={idx}>
          <TextV2>{title}</TextV2>
        </li>
      ))}
    </StyledUL>
  );
  return (
    <div id="change-language-and-pronoun-content-container">
      {profileTitles.length ? (
        <>
          <TextV2>
            {t(
              'ui.event-management.learner.purchase.latest-evaluator.edit.modal.body',
            )}
          </TextV2>
          {renderProfileTitles(profileTitles)}
        </>
      ) : null}
      <StyledLabel sx={{ mt: profileTitles.length ? spacingSizeMap.S : 0 }}>
        {t(
          'ui.event-management.events.download-or-share-profiles.prefered-language-option',
        )}
      </StyledLabel>
      <SelectDialectControl
        options={profileDialectOptions}
        dialect={selectedDialect}
        setDialect={handleDialectSelection}
        fullWidth
      />
      <StyledLabel sx={{ mt: spacingSizeMap.S }}>
        {t(
          'ui.event-management.learner.purchase.latest-evaluator.edit.modal.pronouns.label',
        )}
      </StyledLabel>
      <RadioGroupControl<Pronoun>
        name="pronouns"
        variant="list"
        options={getPronounOptions(
          t,
          DIALECTS[selectedDialect]?.supportsInclusivePronouns,
        )}
        onChange={setPronoun}
        value={pronoun}
        title={'Pronouns selection'}
      />
    </div>
  );
};

export const ChangeLanguageAndPronounsModal = ({
  open,
  onClose,
  organisationId,
  learnerId,
  evaluationId,
  pronoun: initialPronoun,
  preferredDialect: initialPreferredDialect,
  profiles,
}: ChangeLanguageAndPronounsModalProps) => {
  const [pronoun, setPronoun] = useState<Pronoun>(initialPronoun);
  const [preferredProfileDialect, setSelectedDialect] =
    useState<AllDialectOptions>(initialPreferredDialect);

  const { t, i18n } = useTranslation();
  const { language: locale } = i18n;

  const queryClient = useQueryClient();
  const { mutate: setPronounsAndDialect } = useSetPronounsAndPreferredDialect(
    queryClient,
    onClose,
  );
  const onConfirm = () => {
    setPronounsAndDialect({
      pronoun,
      preferredProfileDialect,
      organisationId,
      learnerId,
      evaluationId,
    });
  };
  const resetState = () => {
    setPronoun(initialPronoun);
    setSelectedDialect(initialPreferredDialect);
  };

  const profileDialects = Array.from(
    new Set(profiles.flatMap((profile) => profile.supportedDialects)),
  );
  const profileTypes = Array.from(
    new Set(profiles.flatMap((profile) => profile.type)),
  );
  const profileTitles = profiles.map((profile) =>
    getProfileTitleText(profile.type, profile.evaluationSubmittedOn, locale, t),
  );

  const profileDialectOptions = getProfileDialectOptions({
    selectedPronoun: pronoun,
    profileDialects,
    profileTypes,
    initialPronoun,
  });

  return (
    <Dialog
      open={open}
      title={t(
        'ui.event-management.learner.purchase.latest-evaluator.edit.modal.heading',
      )}
      onClose={() => {
        resetState();
        onClose();
      }}
      content={
        <ChangeLanguageAndPronounsModalContent
          initialPronoun={initialPronoun}
          pronoun={pronoun}
          setPronoun={setPronoun}
          setSelectedDialect={setSelectedDialect}
          selectedDialect={preferredProfileDialect}
          profileTitles={profileTitles}
          profileDialectOptions={profileDialectOptions}
        />
      }
      primaryButtonText={t(
        'ui.event-management.learner.purchase.latest-evaluator.edit.modal.button.yes',
      )}
      secondaryButtonText={t('ui.event-management.access.confirm.no-button')}
      onSubmit={onConfirm}
      onClosed={() => {
        resetState();
        onClose();
      }}
    />
  );
};
